// Defensive primitives shared by every collector. Cardinal rule: a RUM script
// must never throw uncaught into the host page. Anything that touches DOM,
// Performance, storage, or network goes through here.

export function safe(fn, fallback) {
  return function () {
    try {
      return fn.apply(this, arguments);
    } catch (_) {
      return fallback;
    }
  };
}

export function safely(fn) {
  try { return fn(); } catch (_) { return undefined; }
}

// requestIdleCallback with a deterministic fallback. Used to push non-urgent
// work (serialisation, beacon prep) off the critical path.
const RIC = typeof requestIdleCallback === 'function' ? requestIdleCallback : null;
export function onIdle(fn, timeout) {
  if (RIC) {
    return RIC(function (deadline) {
      try { fn(deadline); } catch (_) { /* silent */ }
    }, { timeout: timeout || 2000 });
  }
  return setTimeout(function () {
    try { fn({ timeRemaining: function () { return 50; }, didTimeout: true }); } catch (_) { /* silent */ }
  }, 1);
}

// Per-API feature detection. Cached because these queries are hot (every
// collector calls them at init), and detection itself can throw (e.g. accessing
// PerformanceObserver.supportedEntryTypes in old Safari).
const _supports = {};
export function supports(key) {
  if (key in _supports) return _supports[key];
  let ok = false;
  try {
    switch (key) {
      case 'PerformanceObserver': ok = typeof PerformanceObserver === 'function'; break;
      case 'sendBeacon':          ok = !!(navigator && navigator.sendBeacon); break;
      case 'fetchKeepalive':      ok = (function () {
        try { return 'keepalive' in new Request(''); } catch (_) { return false; }
      })(); break;
      case 'visibilityChange':    ok = typeof document !== 'undefined' && 'visibilityState' in document; break;
      case 'connection':          ok = !!(navigator && (navigator.connection || navigator.mozConnection || navigator.webkitConnection)); break;
      case 'deviceMemory':        ok = !!(navigator && 'deviceMemory' in navigator); break;
      case 'userAgentData':       ok = !!(navigator && navigator.userAgentData); break;
      case 'storage':             ok = (function () {
        try { var k = '__amb_t'; localStorage.setItem(k, '1'); localStorage.removeItem(k); return true; } catch (_) { return false; }
      })(); break;
      case 'sessionStorage':      ok = (function () {
        try { var k = '__amb_t'; sessionStorage.setItem(k, '1'); sessionStorage.removeItem(k); return true; } catch (_) { return false; }
      })(); break;
      case 'crypto':              ok = !!(typeof crypto !== 'undefined' && crypto.randomUUID);
        break;
      case 'compressionStream':   ok = typeof CompressionStream === 'function'; break;
    }
  } catch (_) { ok = false; }
  _supports[key] = ok;
  return ok;
}

// PerformanceObserver wrapper that swallows unsupported entry types instead of
// throwing (Safari pre-16 throws on unknown types; Firefox returns empty).
export function observePerf(type, callback, opts) {
  if (!supports('PerformanceObserver')) return null;
  try {
    const po = new PerformanceObserver(function (list) {
      try { callback(list.getEntries(), po); } catch (_) { /* silent */ }
    });
    const init = Object.assign({ type: type, buffered: true }, opts || {});
    po.observe(init);
    return po;
  } catch (_) {
    return null;
  }
}

export function uuid() {
  if (supports('crypto')) {
    try { return crypto.randomUUID(); } catch (_) { /* fall through */ }
  }
  // RFC 4122 v4 — only used if crypto.randomUUID is missing (old Safari).
  const r = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(r);
  } else {
    for (let i = 0; i < 16; i++) r[i] = (Math.random() * 256) | 0;
  }
  r[6] = (r[6] & 0x0f) | 0x40;
  r[8] = (r[8] & 0x3f) | 0x80;
  const h = Array.from(r, (b) => b.toString(16).padStart(2, '0'));
  return `${h.slice(0,4).join('')}-${h.slice(4,6).join('')}-${h.slice(6,8).join('')}-${h.slice(8,10).join('')}-${h.slice(10,16).join('')}`;
}

export function now() {
  // Date.now is fine for wall-clock event timestamps. performance.now is for
  // intra-page timing diffs (which web-vitals handles itself).
  return Date.now();
}
